import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray, AbstractControl } from '@angular/forms';
import { Practitioner } from 'src/app/shared/models/practitioner.model';
import { faStar  } from '@fortawesome/free-solid-svg-icons';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { Address } from 'src/app/shared/models/address.model';
import { Education } from 'src/app/shared/models/education.model';
import { Specialty } from 'src/app/shared/models/specialty.model';
import { Therapy } from 'src/app/shared/models/therapy.model';
import { Subscription, Observable } from 'rxjs';
import { Testimonial } from 'src/app/shared/models/testimonial.model';
import { TestimonialService } from 'src/app/shared/services/testimonial.service';
import { distinctUntilChanged, debounceTime, switchMap } from 'rxjs/operators';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { ModalNotificationComponent } from 'src/app/shared/components/modal-notification/modal-notification.component';
import { PractitionerService } from 'src/app/shared/services/practitioner.service';
import { PractitionerStatus } from 'src/app/shared/enums/practitioner-status.enum';
import { Document } from 'src/app/shared/models/document.model';
import { DocumentType } from 'src/app/shared/enums/document-type.enum';

@Component({
  selector: 'app-practitioner-create',
  templateUrl: './practitioner-create.component.html',
  styleUrls: ['./practitioner-create.component.scss']
})

export class PractitionerCreateComponent implements OnInit, OnDestroy {
  practitionerForm: FormGroup;
  password: string = '';
  genders = ['Male', 'Female', 'Non-binary'];
  insuranceTypes = ['Private', 'Public', 'Private and Public'];
  healthCategories = ['Mental well-being', 'Physical well-being', 'Both'];
  languages = ['Select...', 'English', 'German', 'Arabic', 'Spanish', 'French', 'Chinese', 'Portuguese', 'Russian', 'Turkish', 'Hindi', 'Vietnamese'];
  bodySpecialties: string[] = [];
  mindSpecialties: string[] = [];
  bodyConditions: string[] = [];
  mindConditions: string[] = [];
  professionalBodies: string[] = [];
  accreditedRegisters: string[] = [];
  starIcon = faStar;
  address: Address;
  profileImage: File;
  profileImageUrl: string | ArrayBuffer;
  disableSubmit: boolean = false;
  isLoading: boolean = false;
  error: string = null;
  isEdit: boolean = false;
  registerSubscription: Subscription;
  specialtyTypeahead;
  currentWindowWidth: number;
  @ViewChild('confirmation') confirmationModal: ModalNotificationComponent;

  get isMobileScreen(): boolean {
    return this.currentWindowWidth <= 420;
  }

  constructor(
    private testimonialService: TestimonialService,
    private lookupService: LookupService,
    private practitionerService: PractitionerService,
    private router: Router,
    private route: ActivatedRoute) { }

  ngOnInit(): void {
    this.currentWindowWidth = window.innerWidth;
    this._createPractitionerForm();
    this._maybePopulateFromEdit();
    this._setupTypeaheads();
    this._loadDrodowns();
  }

  getSpecialtiesForCategory(category: string): string[] {
    category = !category ? 'body' : category;
    return category === 'body' ? this.bodySpecialties : this.mindSpecialties;
  }

  _loadDrodowns() {
    this.lookupService.searchSpecialties('body').then(values => {
      this.bodySpecialties = values;
      this.bodySpecialties.unshift('Select...');
      this.bodySpecialties.push('Other...');
    });

    this.lookupService.searchSpecialties('mind').then(values => {
      this.mindSpecialties = values;
      this.mindSpecialties.unshift('Select...');
      this.mindSpecialties.push('Other...');
    });

    this.lookupService.searchConditions('body').then(values => {
      this.bodyConditions = values;
      this.bodyConditions.unshift('Select...');
      this.bodyConditions.push('Other...');
    });

    this.lookupService.searchConditions('mind').then(values => {
      this.mindConditions = values;
      this.mindConditions.unshift('Select...');
      this.mindConditions.push('Other...');
    });

    this.lookupService.searchProfessionalBodies().then(values => {
      this.professionalBodies = values;
      this.professionalBodies.unshift('Select...');
      this.professionalBodies.push('Other...');
    });

    this.lookupService.searchAccreditedRegisters().then(values => {
      this.accreditedRegisters = values;
      this.accreditedRegisters.unshift('Select...');
      this.accreditedRegisters.push('Other...');
    })
  }

  _createPractitionerForm(): void {
    this.practitionerForm = new FormGroup({
      'userData': new FormGroup({
        'profileImage': new FormControl(null, Validators.required),
        'salutation': new FormControl(null),
        'firstName': new FormControl(null, Validators.required),
        'lastName': new FormControl(null, Validators.required),
        'email': new FormControl(null, [Validators.email, Validators.required]),
        'phoneNumber': new FormControl(null, [Validators.required, Validators.pattern('[- +()0-9]+')]),
        'gender': new FormControl(null, Validators.required),
        'title': new FormControl(null, Validators.required),
        'bio': new FormControl(null, [Validators.required, Validators.maxLength(700)]),
        'approach': new FormControl(null, [Validators.required, Validators.maxLength(700)])
      }),
      'experienceData': new FormGroup({
        'education': new FormArray([
          new FormControl(null, Validators.required)
        ]),
        'degree': new FormArray([
          new FormControl(null, Validators.required)
        ]),
        'licenses': new FormArray([
          new FormControl(null, Validators.required)
        ]),
        'languages': new FormArray([
          new FormControl(null, [Validators.required, this.containsSelect.bind(this)])
        ]),
        'professionalBodies': new FormArray([
          new FormControl(null)
        ]),
        'accreditedRegisters': new FormArray([
          new FormControl(null)
        ])
      }),
      'serviceData': new FormGroup({
        'categories': new FormArray([
          new FormControl('body', Validators.required)
        ]),
        'specialties': new FormArray([
          new FormControl(null, [Validators.required, this.containsSelect.bind(this)])
        ]),
        'specialtyData': new FormArray([
          new FormGroup({
            'therapies': new FormArray([
              new FormControl(null, Validators.required)
            ]),
            'costs': new FormArray([
              new FormControl(null, [Validators.required, Validators.min(1)])
            ]),
            'descriptions': new FormArray([
              new FormControl(null, Validators.required)
            ]),
            'durations': new FormArray([
              new FormControl(null, [Validators.required, Validators.min(1)])
            ])
          })
        ]),
        'conditionCategories': new FormArray([
          new FormControl('body', Validators.required)
        ]),
        'conditions': new FormArray([
          new FormControl(null, [Validators.required, this.containsSelect.bind(this)])
        ])
      }),
      'businessData': new FormGroup({
        'organization': new FormControl(null),
        'offersVirtual': new FormControl(null, Validators.required),
        'healthCategory': new FormControl(null, Validators.required),
        'testimonials': new FormArray([
          new FormControl(null, [Validators.email]),
          new FormControl(null, [Validators.email]),
          new FormControl(null, [Validators.email])
        ])
      })
    });
  }

  _maybePopulateFromEdit(): void {
    if (this.router.url.indexOf('edit') === -1) { return; }
    this.isEdit = true;
    this.route.params.subscribe((params: Params) => {
      this.practitionerService.getPractitioner(params['id']).subscribe((practitioner: Practitioner) => {
        this.practitionerForm.patchValue({
          userData: {
            firstName: practitioner.firstName,
            lastName: practitioner.lastName,
            email: practitioner.email,
            gender: practitioner.gender,
            title: practitioner.title,
            bio: practitioner.bio
          },
          experienceData: {
            education: this._populateEducation(practitioner),
            degree: practitioner.education.map(e => e.degree)
          }
        });
      }, error => {
        alert(error);
      });
    });
  }

  _populateEducation(practitioner: Practitioner): string[] {
    if (practitioner.education.length > 1) {
      for (var i = 0; i < practitioner.education.length - 1; i++) {
        this.onAddControl('experienceData.education');
        this.onAddControl('experienceData.degree');
      }
    }

    return practitioner.education.map(e => e.name);
  }

  _setupTypeaheads(): void {
    this.specialtyTypeahead = (text: Observable<string>) => text.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(term => term.length < 2 ? []
        : this.lookupService.searchSpecialties(term))
    )
  }

  _mapValidationErrors(): void {
    setTimeout(_ => {
      let html = '<ul>';
      let errors = document.getElementsByClassName('help-block');
      for (let i = 0; i < errors.length; i++) {
        html += '<li>' + errors.item(i).innerHTML + '</li>';
      }
      html = html + '</ul>';
      this.error = html;
    }, 100);
  }

  onSubmit(): void {
    if (this.practitionerForm.invalid) {
      this.practitionerForm.markAllAsTouched();
      this._mapValidationErrors();
      return;
    }
    const value = this.practitionerForm.value;
    this.address.organization = value.businessData.organization;

    var practitioner = new Practitioner(
      value.userData.approach,
      value.userData.bio,
      value.serviceData.conditions,
      this._mapDocuments(),
      this._mapEducations(),
      value.userData.email,
      value.userData.firstName,
      value.userData.gender,
      this._mapHealthCategory(),
      null,
      false,
      value.experienceData.languages,
      value.userData.lastName,
      value.experienceData.licenses,
      this.address,
      value.businessData.offersVirtual,
      value.userData.phoneNumber,
      null,
      null,
      value.userData.salutation,
      this._mapSpecialties(),
      PractitionerStatus.PendingApproval,
      value.userData.title,
      null,
      new Date().getTime(),
      new Date().getTime(),
      null
    );

    this.disableSubmit = true;
    this.isLoading = true;
    this.error = null;

    this.practitionerService.createPractitioner(practitioner, this.profileImage).then((prac: Practitioner) => {
      this.testimonialService.sendTestimonialEmail(prac, this._mapTestimonials());
      this.router.navigate(['/home']);
      this.confirmationModal.open('Thank you!', 'Your application has been submitted. You\'ll be receiving an email shortly.', 'Okay');
      this.isLoading = false;
    }, error => {
      this.error = error;
      this.disableSubmit = false;
      this.isLoading = false;
    });
  }

  _mapDocuments(): Document[] {
    var documents: Document[] = [];
    var value = this.practitionerForm.value.experienceData;
    value.professionalBodies.forEach((document: string) => {
      if (document && document.toLowerCase().indexOf('select...') < 0) {
        documents.push(new Document(document, DocumentType.ProfessionalBody, ''));
      }
    });
    value.accreditedRegisters.forEach((document: string) => {
      if (document && document.toLowerCase().indexOf('select...') < 0) {
        documents.push(new Document(document, DocumentType.AccreditedRegister, ''));
      }
    });
    return documents;
  }

  _mapEducations(): Education[] {
    var educations: Education[] = [];
    var value = this.practitionerForm.value.experienceData;
    value.education.forEach((education: string, index: number) => {
      educations.push(new Education(education, value.degree[index]));
    });

    return educations;
  }

  _mapSpecialties(): Specialty[] {
    var specialties: Specialty[] = [];
    var value = this.practitionerForm.value.serviceData;
    value.specialties.forEach((specialty: string, sIndex: number) => {
      var therapies: Therapy[] = [];
      var category: string = value.categories[sIndex];
      value.specialtyData[sIndex].therapies.forEach((name: string, tIndex: number) => {
        therapies.push(new Therapy(
          name,
          value.specialtyData[sIndex].costs[tIndex],
          value.specialtyData[sIndex].descriptions[tIndex],
          value.specialtyData[sIndex].durations[tIndex]));
      })
      specialties.push(new Specialty(category, specialty, therapies));
    });

    return specialties;
  }

  _mapHealthCategory(): string[] {
    var categories: string[] = [];
    var value: string = this.practitionerForm.value.businessData.healthCategory;

    if (value.toLowerCase().indexOf('both') === -1) {
      categories.push(value)
    } else {
      categories.push('Physical well-being');
      categories.push('Mental well-being');
    }

    return categories;
  }

  _mapTestimonials(): Testimonial[] {
    var testimonials: Testimonial[] = [];
    var value = this.practitionerForm.value.businessData;
    value.testimonials.forEach((email: string) => {
      if (email) {
        testimonials.push(new Testimonial(null, email, null, null, null, null, null, null, null));
      }
    });

    return testimonials;
  }

  _filterLanguages(index: number): string[] {
    var selectedLanguages: string[] = this.practitionerForm.value.experienceData.languages;
    if (selectedLanguages.length === 0) {
      return this.languages;
    }

    var filteredLanguages = this.languages.filter(l => {
      return !selectedLanguages.includes(l) || l === selectedLanguages[index];
    });

    return filteredLanguages;
  }

  updateProfessionalBody(index: number, value: string) {
    let isOtherProfessionalBody = value.toLowerCase() === 'other...';
    let newValue = isOtherProfessionalBody ? '' : value;
    let currentProfessionalBodies: string[] = [...this.practitionerForm.value.experienceData.professionalBodies];
    currentProfessionalBodies[index] = newValue;
    this.practitionerForm.patchValue({
      experienceData: {
        professionalBodies: currentProfessionalBodies
      }
    });
    document.getElementById('professionalBody' + index).hidden = !isOtherProfessionalBody;
  }

  updateAccreditedRegister(index: number, value: string) {
    let isOtherAccreditedRegister = value.toLowerCase() === 'other...';
    let newValue = isOtherAccreditedRegister ? '' : value;
    let currentAccreditedRegisters: string[] = [...this.practitionerForm.value.experienceData.accreditedRegisters];
    currentAccreditedRegisters[index] = newValue;
    this.practitionerForm.patchValue({
      experienceData: {
        accreditedRegisters: currentAccreditedRegisters
      }
    });
    document.getElementById('accreditedRegister' + index).hidden = !isOtherAccreditedRegister;
  }

  updateSpecialtyCategory(index: number, value: string) {
    let currentCategories: string[] = [...this.practitionerForm.value.serviceData.categories];
    currentCategories[index] = value;
    this.practitionerForm.patchValue({
      serviceData: {
        categories: currentCategories
      }
    });
  }

  updateSpecialty(index: number, value: string) {
    let isOtherSpecialty = value.toLowerCase() === 'other...';
    let newValue = isOtherSpecialty ? '' : value;
    let currentSpecialties: string[] = [...this.practitionerForm.value.serviceData.specialties];
    currentSpecialties[index] = newValue;
    this.practitionerForm.patchValue({
      serviceData: {
        specialties: currentSpecialties
      }
    });
    document.getElementById('specialty' + index).hidden = !isOtherSpecialty;
  }

  updateCondition(index: number, value: string) {
    let isOtherCondition = value.toLowerCase() === 'other...';
    let newValue = isOtherCondition ? '' : value;
    let currentConditions: string[] = [...this.practitionerForm.value.serviceData.conditions];
    currentConditions[index] = newValue;
    this.practitionerForm.patchValue({
      serviceData: {
        conditions: currentConditions
      }
    });
    document.getElementById('condition' + index).hidden = !isOtherCondition;
  }

  updateConditionCategory(index: number, value: string) {
    let currentCategories: string[] = [...this.practitionerForm.value.serviceData.conditionCategories];
    currentCategories[index] = value;
    this.practitionerForm.patchValue({
      serviceData: {
        conditionCategories: currentCategories
      }
    });
  }

  getImage(event: any) {
    if (event.target.files.length === 0)
      return;

    var reader = new FileReader();
    this.profileImage = event.target.files[0];
    reader.readAsDataURL(event.target.files[0]);
    reader.onload = (_event) => {
      this.profileImageUrl = reader.result;
    }
  }

  getAddress(place: google.maps.places.PlaceResult) {
    this.address = Address.createAddressFromPlace(place);
  }

  setLookupValue(value: string, path: string) {
    this.practitionerForm.get(path).setValue(value);
    this.practitionerForm.get(path).markAsTouched();
    this.practitionerForm.get(path).updateValueAndValidity();
  }

  onAddControl(type: string) {
    (<FormArray>this.practitionerForm.get(type)).push(new FormControl(null));
  }

  onAddSpecialtyDataControl() {
    (<FormArray>this.practitionerForm.get('serviceData.specialtyData')).push(new FormGroup({
      'therapies': new FormArray([
        new FormControl(null, Validators.required)
      ]),
      'costs': new FormArray([
        new FormControl(null, [Validators.required, Validators.min(1)])
      ]),
      'descriptions': new FormArray([
        new FormControl(null, Validators.required)
      ]),
      'durations': new FormArray([
        new FormControl(null, [Validators.required, Validators.min(1)])
      ])
    }));
  }

  onRemoveControl(type: string, index: number) {
    (<FormArray>this.practitionerForm.get(type)).removeAt(index);
  }

  getControls(type: string): AbstractControl[] {
    return (<FormArray>this.practitionerForm.get(type)).controls;
  }

  ngOnDestroy(): void {
    if (!this.registerSubscription) { return; }
    this.registerSubscription.unsubscribe();
  }

  @HostListener('window:resize')
  onResize() {
    this.currentWindowWidth = window.innerWidth;
  }

  containsSelect(control: FormControl): {[s:string]: boolean} {
    if (control.value && control.value.toLowerCase().indexOf('select') > -1) {
      return {'containsSelect': true};
    }
    return null;
  }
}
